// New-order sound: the PopCart sound logo, "bl-OOP!" (Bold Pop manual §12): G5 → D6 in 80 ms, then a
// dry cork pop on the burst frame, 450 ms. The file is public/sounds/pop-logo.wav, rendered by
// apps/customer/scripts/make-sting.mjs (the same bytes the apps ship). Played through Web Audio.
// Browsers only allow audio after a user gesture, so the context is created/resumed on the first
// pointer or key press and reused after that. Respects the "New-order sound" switch (the callers
// check it, and so does this module, from the saved preference). Never throws.

export const POP_LOGO_URL = `${import.meta.env?.BASE_URL ?? '/'}sounds/pop-logo.wav`;
/** The sound logo's length in seconds (kernel motion.sound.ms = 450). */
export const POP_LOGO_S = 0.45;
const MUTED_KEY = 'popcart.backoffice.muted';

type Ctx = AudioContext;
let ctx: Ctx | null = null;
let buffer: AudioBuffer | null = null;
let loading: Promise<AudioBuffer | null> | null = null;
/** ctx.currentTime when the last sound started, so two alerts never stack. */
let lastStart = Number.NEGATIVE_INFINITY;

function getCtx(): Ctx | null {
  if (ctx) return ctx;
  if (typeof window === 'undefined') return null;
  const AC: typeof AudioContext | undefined =
    window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AC) return null;
  try {
    ctx = new AC();
  } catch {
    ctx = null;
  }
  return ctx;
}

function mutedSaved(): boolean {
  try {
    return window.localStorage.getItem(MUTED_KEY) === '1';
  } catch {
    return false;
  }
}

/** Fetch and decode the WAV once; a failed load is retried on the next call. */
function load(c: Ctx): Promise<AudioBuffer | null> {
  if (buffer) return Promise.resolve(buffer);
  if (!loading) {
    loading = (async () => {
      try {
        const res = await fetch(POP_LOGO_URL);
        if (!res.ok) return null;
        const data = await res.arrayBuffer();
        buffer = await c.decodeAudioData(data);
        return buffer;
      } catch {
        return null;
      } finally {
        loading = null;
      }
    })();
  }
  return loading;
}

function start(c: Ctx, b: AudioBuffer): boolean {
  if (c.currentTime - lastStart < POP_LOGO_S) return false;
  try {
    const src = c.createBufferSource();
    src.buffer = b;
    src.connect(c.destination);
    src.start();
    lastStart = c.currentTime;
    return true;
  } catch {
    return false;
  }
}

/** Call once at startup: unlocks audio on the first interaction and preloads the sound. */
export function primeAudioOnGesture() {
  const unlock = () => {
    const c = getCtx();
    if (c) {
      if (c.state === 'suspended') void c.resume().catch(() => {});
      void load(c);
    }
    window.removeEventListener('pointerdown', unlock);
    window.removeEventListener('keydown', unlock);
  };
  window.addEventListener('pointerdown', unlock);
  window.addEventListener('keydown', unlock);
}

/** Play the sound logo (unless the sound is switched off). Resolves to whether it played; never rejects. */
export async function playPopLogo(): Promise<boolean> {
  if (mutedSaved()) return false;
  const c = getCtx();
  if (!c) return false;
  if (c.state === 'suspended') void c.resume().catch(() => {});
  const b = buffer ?? (await load(c));
  if (!b || mutedSaved()) return false;
  return start(c, b);
}

/** New order on Today or the grill board: the sound logo, fire and forget. */
export function playNewOrderTone(): void {
  void playPopLogo();
}

/** Tests only: forget the context, the decoded sound and the last start. */
export function resetSoundForTests() {
  ctx = null;
  buffer = null;
  loading = null;
  lastStart = Number.NEGATIVE_INFINITY;
}
